import { css } from 'aphrodite';
import React, { useMemo } from 'react';
import { Button } from '../../../../../web/components-v2/Button';
import { IDonationSurvey } from '../../../../models/Api';
import { DonationFrequency } from '../../../../models/enums/DonationFrequency';
import { styleSheet } from './styles';

type Props = {
	/** Currently selected donation frequency (null = one-time) */
	value: DonationFrequency | null;
	/** Callback when frequency selection changes */
	onChange: (frequency: DonationFrequency | null) => void;
	/** Array of all supported recurring frequencies */
	supportedRecurringFrequencies: DonationFrequency[];
	/** Survey object containing frequency configuration */
	survey?: IDonationSurvey;
};

/**
 * FrequencySelector Component
 *
 * Displays donation frequency options as either:
 * - A row of buttons when multiple frequencies are available
 * - Informational text when only one frequency option is available
 *
 * Behavior:
 * - Shows "One time" button only if oneTimeDonationSettings.enabled is true (defaults to true for backward compatibility)
 * - Only shows recurring frequency buttons if they are enabled in the survey settings
 * - Returns null if no frequencies are enabled at all
 * - Auto-selects first available recurring frequency if one-time is disabled and nothing is selected
 * - When only one frequency is available, displays text instead of buttons:
 *   - "This is a one-time donation." (when only one-time is enabled)
 *   - "This is a monthly recurring donation." (when only monthly is enabled)
 *   - "This is a yearly recurring donation." (when only yearly is enabled)
 * - Dynamically adjusts grid layout based on number of enabled frequencies
 *
 * State Management:
 * - `value === null` represents one-time donation
 * - `value === DonationFrequency.Monthly` or `DonationFrequency.Yearly` represents recurring
 *
 * @param value - Currently selected frequency (null for one-time)
 * @param onChange - Callback when frequency selection changes
 * @param supportedRecurringFrequencies - Array of all supported frequencies
 * @param survey - Survey object containing frequency configuration
 */
export const FrequencySelector = ({ value, onChange, supportedRecurringFrequencies, survey }: Props) => {
	// Filter to only show enabled frequencies
	// Checks the survey's frequency settings to determine which buttons to display
	// Falls back to showing all supported frequencies if survey config is unavailable
	const enabledFrequencies = useMemo(() => {
		if (!survey?.donationOptions) {
			return supportedRecurringFrequencies;
		}

		return supportedRecurringFrequencies.filter(freq => {
			if (freq === DonationFrequency.Monthly) {
				return survey.donationOptions.monthlyDonationSettings?.enabled;
			}
			if (freq === DonationFrequency.Yearly) {
				return survey.donationOptions.annualDonationSettings?.enabled;
			}
			return false;
		});
	}, [survey, supportedRecurringFrequencies]);

	// Check if one-time donations are enabled
	// Defaults to true if oneTimeDonationSettings doesn't exist (backward compatibility)
	const oneTimeEnabled = survey?.donationOptions?.oneTimeDonationSettings?.enabled ?? true;

	// Auto-select first available frequency if one-time is disabled and nothing is selected
	// This ensures a frequency is always selected when one-time is not an option
	// NOTE: This hook must be called before any early returns to comply with React Hooks rules
	// NOTE: onChange must be memoized by the parent component (via useCallback) to prevent
	// infinite re-renders. The parent component (DonationForm) properly memoizes this callback.
	React.useEffect(() => {
		if (!oneTimeEnabled && value === null && enabledFrequencies.length > 0) {
			// Select the first enabled recurring frequency
			onChange(enabledFrequencies[0]);
		}
	}, [oneTimeEnabled, value, enabledFrequencies, onChange]);

	// If no frequencies are enabled at all, don't render anything
	if (!oneTimeEnabled && enabledFrequencies.length === 0) {
		return null;
	}

	const frequencyLabels = {
		[DonationFrequency.Monthly]: 'Monthly',
		[DonationFrequency.Yearly]: 'Yearly',
	};

	// Calculate total number of frequency options to display
	const totalOptions = (oneTimeEnabled ? 1 : 0) + enabledFrequencies.length;

	// If only one option is available, show text instead of buttons
	if (totalOptions === 1) {
		let frequencyText = '';
		if (oneTimeEnabled && enabledFrequencies.length === 0) {
			frequencyText = 'This is a one-time donation.';
		} else if (!oneTimeEnabled && enabledFrequencies.length === 1) {
			const frequency = enabledFrequencies[0];
			frequencyText = `This is a ${frequencyLabels[frequency].toLowerCase()} recurring donation.`;
		}

		return (
			<section className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.singleFrequencyText)}>{frequencyText}</h3>
			</section>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.subtitle)}>Choose donation frequency</h3>
			<div
				className={css(styleSheet.frequenciesContainer)}
				style={{ gridTemplateColumns: `repeat(${totalOptions}, 1fr)` }}
			>
				{/* One-time donation option - only show if enabled */}
				{oneTimeEnabled && (
					<Button
						variant={value === null ? 'primary' : 'secondary'}
						styles={[styleSheet.frequencyButton]}
						type='button'
						onClick={() => onChange(null)}
					>
						One time
					</Button>
				)}

				{/* Recurring donation options - only show enabled frequencies */}
				{enabledFrequencies.map(frequency => (
					<Button
						variant={value === frequency ? 'primary' : 'secondary'}
						key={`frequency-${frequency}`}
						styles={[styleSheet.frequencyButton]}
						type='button'
						onClick={() => onChange(frequency)}
					>
						{frequencyLabels[frequency]}
					</Button>
				))}
			</div>
		</div>
	);
};
